// FEAT-023: 바우처 출력 템플릿 편집기
// - 법인(Subsidiary) 관리자 전용
// - 배경 이미지 업로드/제거 + QR 위치(비율) 드래그 편집
// - 저장 시 SaveVoucherTemplate dispatch, 원복 시 ResetVoucherTemplateToHqDefault

import { selectLicenseProductState } from '../../../reducers/SmartDentReducer';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { Rnd } from 'react-rnd';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import {
    clampQrBox,
    clampTypeBox,
    clampBgBox,
    clampMargins,
    isSafeCompanyId,
} from '../../../lib/voucherTemplate';
import { Translate as t } from '../../../config/Translate';
// PR 리뷰 Major: reason 코드를 `t(rawCode)` 로 직접 번역하던 fail-open 경로를 SSoT 로 흡수.
import { VOUCHER_TEMPLATE_ERROR_MESSAGES, resolveLicenseErrorMessage } from '../../../lib/licenseErrorMessages';
import QrPositionOverlay from '../../../components/voucher/QrPositionOverlay';
import { useImageUpload } from '../../../hooks/useImageUpload';
import { useLicenseProduct } from '../../../hooks/useLicenseProduct';
import { useVoucherTemplateLoader } from '../../../hooks/useVoucherTemplateLoader';
import { useResetConfirmModal } from '../../../hooks/useResetConfirmModal';
// IMP-438: 날짜(전용) 포맷 SSoT — 앱 언어 locale 로 렌더.
import { formatLicenseDateOnly } from '../../../lib/licenseHelper';

const VoucherTemplateEditor = ({ user, voucherTemplate, product: productProp }) => {
    const dispatch = useDispatch();
    const product = useLicenseProduct(productProp);
    const companyId = user?.companyId;
    // IMP-107: 책임을 커스텀 훅으로 분리 — 로드/폼상태(loader), 이미지 업로드(upload), reset 모달.
    const {
        qrBox, setQrBox,
        typeBox, setTypeBox,
        bgUrl, setBgUrl,
        orientation, setOrientation,
        bgBox, setBgBox,
        margins, setMargins,
    } = useVoucherTemplateLoader(companyId, voucherTemplate, product);
    const upload = useImageUpload();

    // 새 파일 선택 시 imageAspect/canvasSize 가 갱신된 다음 자동 Fit 실행하기 위한 트리거.
    const [pendingFit, setPendingFit] = useState(false);
    // 이미지 natural aspect ratio (W/H) — Rnd lockAspectRatio 인자로 사용.
    // null: 아직 로드 안됨 / 또는 미리보기 없음. 이미지 onload 시점에만 실제 비율 set.
    // 자동 Fit 은 이 값이 실제 이미지 비율로 갱신된 뒤 실행되어야 비율 깨짐 방지.
    const [imageAspect, setImageAspect] = useState(null);
    // 캔버스 컨테이너 px 측정용 (Rnd 가 px 기준)
    const canvasRef = useRef(null);
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    // 새 파일 미리보기(upload.previewUrl) 우선, 없으면 로드된 배경 URL.
    const previewUrl = upload.previewUrl || bgUrl;

    // 캔버스 컨테이너 크기 측정 — Rnd 의 px 좌표 ↔ % 변환에 필요
    useEffect(() => {
        const measure = () => {
            if (canvasRef.current) {
                const r = canvasRef.current.getBoundingClientRect();
                setCanvasSize({ width: r.width, height: r.height });
            }
        };
        measure();
        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(measure);
            if (canvasRef.current) ro.observe(canvasRef.current);
            return () => ro.disconnect();
        }
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [orientation]);

    // 이미지 자연 비율 측정 — preview URL 변경 시 갱신.
    // 변경 즉시 null 로 초기화 (이전 이미지 비율 stale 방지) → onload 후 실제 비율 set.
    // 자동 Fit useEffect 는 imageAspect 가 truthy 일 때만 실행되므로 새 이미지 비율 보장.
    useEffect(() => {
        if (!previewUrl) {
            setImageAspect(null);
            return;
        }
        setImageAspect(null);
        const img = new Image();
        let cancelled = false;
        img.onload = () => {
            // 빠른 파일 변경 시 이전 이미지 onload 가 새 이미지 비율을 덮어쓰지 않도록 cancel 가드.
            if (cancelled) return;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setImageAspect(img.naturalWidth / img.naturalHeight);
            }
        };
        img.src = previewUrl;
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [upload.previewUrl, bgUrl]);

    // 파일 선택 — 검증/미리보기는 useImageUpload 가 담당. 성공 시 message 초기화 + 자동 Fit 트리거.
    const handleFileChange = useCallback(async (e) => {
        setError(null);
        setMessage(null);
        const picked = await upload.handleFileChange(e);
        if (picked) setPendingFit(true);
    }, [upload]);

    const handleSave = async () => {
        if (!companyId) return;
        // companyId 경로 탈출 방어 — S3 key prefix 에 삽입 전 whitelist 패턴 검증
        if (!isSafeCompanyId(companyId)) {
            setError(t('company.message.invalidCompanyId'));
            return;
        }
        setError(null);
        setMessage(null);
        setSaving(true);
        try {
            let uploadedUrl = bgUrl;
            let checksum = voucherTemplate?.imageChecksum ?? null;

            if (upload.file) {
                // SHA-256(UI 비교용) + S3 업로드를 useImageUpload 가 수행.
                const res = await upload.uploadToS3(companyId);
                checksum = res?.checksum ?? null;
                uploadedUrl = res?.url ?? null;
                if (!uploadedUrl) {
                    setError(t('io.message.error.imageUploadFailed'));
                    setSaving(false);
                    return;
                }
            }

            const result = await dispatch(SmartDentAction.SaveVoucherTemplate(companyId, {
                backgroundImageUrl: uploadedUrl,
                imageChecksum: checksum,
                qrBox,
                typeBox,
                orientation,
                bgBox,
                margins,
                updater: user?.adminid ?? null,
                // 낙관적 잠금 — Get 시점의 updated 값을 넘겨 concurrent Save 충돌 검출.
                // voucherTemplate 이 synthetic(최초 진입) 인 경우 undefined 가 전달되면
                // Action 레이어에서 null 로 정규화되어 신규 레코드 분기를 탄다.
                prevUpdated: voucherTemplate?.updated,
            }, { product }));

            if (result?.ok) {
                upload.reset(); // bgFile/previewUrl 정리 + ObjectURL revoke
                setBgUrl(uploadedUrl);
                setMessage(t('common.message.success.saved'));
            } else {
                // TODO (Phase 2): dispatch 실패 시 방금 업로드한 S3 객체가 orphan 으로 남는다.
                // 현재는 재시도 시 새 timestamp 로 객체가 누적될 수 있으므로 서버측 TTL/청소 Lambda
                // 또는 "저장 성공 후 업로드" staging 구조로 이관 예정 (FEAT-023 후속 이슈).
                if (upload.file && uploadedUrl && uploadedUrl !== bgUrl) {
                    console.warn(
                        '[VoucherTemplateEditor.handleSave] orphan S3 object possible',
                        { uploadedUrl, reason: result?.reason },
                    );
                }
                // 폴백은 dotted `common.message.error.saveFailed` 다 — flat `Save failed` 는
                //   IMP-275 마이그레이션에서 전 lang 파일에서 삭제된 키이고, 회귀 가드
                //   (`imp275.i18n.test.js`)가 lang 잔존과 소스의 정적 flat 호출을 둘 다 막는다.
                //   (그 가드는 주석도 grep 하므로 여기에 flat 키 리터럴을 적지 않는다.)
                //   아래 catch 절도 이미 같은 dotted 키를 쓴다.
                setError(resolveLicenseErrorMessage(
                    VOUCHER_TEMPLATE_ERROR_MESSAGES, result?.reason, null, t,
                    t('common.message.error.saveFailed'),
                ));
            }
        } catch (e) {
            console.error('[VoucherTemplateEditor.handleSave]', e);
            setError(t('common.message.error.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!companyId) return;
        if (!isSafeCompanyId(companyId)) {
            setError(t('company.message.invalidCompanyId'));
            return;
        }
        setError(null);
        setMessage(null);
        setSaving(true);
        try {
            const result = await dispatch(SmartDentAction.ResetVoucherTemplateToHqDefault(
                companyId,
                user?.adminid ?? null,
                {
                    // Reset 과 Edit 경합 시 silent overwrite 방지 (R6 Major/Minor)
                    prevUpdated: voucherTemplate?.updated,
                    product,
                },
            ));
            if (result?.ok) {
                upload.reset(); // bgFile/previewUrl 정리 + ObjectURL revoke
                // qrBox/orientation/bgBox/margins/bgUrl 은 loader useEffect 가 Redux 업데이트 감지해
                // BE 응답(=ancestor HQ 값)으로 동기화. 로컬 기본값 강제 set 제거.
                setMessage(t('common.button.resetToDefault'));
            } else {
                setError(resolveLicenseErrorMessage(
                    VOUCHER_TEMPLATE_ERROR_MESSAGES, result?.reason, null, t, t('Reset failed'),
                ));
            }
        } finally {
            setSaving(false);
        }
    };

    // IMP-107 (AC-3): reset confirm 모달 — confirm 시 모달 닫고 handleReset 실행.
    const resetModalCtl = useResetConfirmModal(handleReset);

    // Fit — 이미지를 마진 영역 안에 비율 유지하며 최대화 + 중앙 배치.
    // imageAspect (W/H) 와 캔버스 px 크기 기준으로 계산해야 A4 비율에서 정확.
    const handleFit = () => {
        if (!canvasSize.width || !canvasSize.height) return;
        const W = canvasSize.width;
        const H = canvasSize.height;
        const { top, right, bottom, left } = margins;
        const areaWPct = Math.max(1, 100 - left - right);
        const areaHPct = Math.max(1, 100 - top - bottom);
        const areaWpx = (areaWPct / 100) * W;
        const areaHpx = (areaHPct / 100) * H;
        const aspect = imageAspect || 1;
        const areaAspect = areaWpx / areaHpx;
        let fittedWpx;
        let fittedHpx;
        if (aspect >= areaAspect) {
            fittedWpx = areaWpx;
            fittedHpx = areaWpx / aspect;
        } else {
            fittedHpx = areaHpx;
            fittedWpx = areaHpx * aspect;
        }
        const fittedWPct = (fittedWpx / W) * 100;
        const fittedHPct = (fittedHpx / H) * 100;
        const xPct = left + (areaWPct - fittedWPct) / 2;
        const yPct = top + (areaHPct - fittedHPct) / 2;
        setBgBox(clampBgBox({ xPct, yPct, widthPct: fittedWPct, heightPct: fittedHPct }));
    };

    // 마진은 내부 상태를 % 로 유지(해상도 무관, 저장/로드 안정).
    // UI 만 캔버스 픽셀로 표시/입력 — 사용자가 직관적으로 값 인지하도록.
    const marginAxis = (key) => (key === 'top' || key === 'bottom')
        ? canvasSize.height
        : canvasSize.width;

    const marginPx = (key) => {
        const dim = marginAxis(key);
        if (!dim) return 0;
        return Math.round((margins[key] / 100) * dim);
    };

    const handleMarginChange = (key) => (e) => {
        const px = Number(e.target.value);
        const dim = marginAxis(key);
        if (!dim) return;
        const pct = (px / dim) * 100;
        setMargins((prev) => clampMargins({ ...prev, [key]: pct }));
    };

    // 새 파일 선택 후 imageAspect/canvasSize 가 모두 준비되면 자동 Fit 실행.
    // handleFit 이 setBgBox 만 호출하므로 의존성으로 안 넣어도 안전 (lint 비활성).
    useEffect(() => {
        if (!pendingFit) return;
        if (!imageAspect || !canvasSize.width || !canvasSize.height) return;
        handleFit();
        setPendingFit(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingFit, imageAspect, canvasSize.width, canvasSize.height]);

    // Orientation 변경 시 모든 박스의 물리적(mm) 크기/위치를 보존하도록 % 좌표를 재계산.
    // A4 portrait = 210 × 297mm, landscape = 297 × 210mm.
    // 페이지 폭(mm)이 달라지므로 같은 %도 다른 물리적 크기가 된다 (= 사용자 불만).
    // 가로 좌표/크기는 oldPageW/newPageW 로, 세로는 oldPageH/newPageH 로 스케일링.
    const handleOrientationChange = (next) => {
        if (next === orientation) return;
        const oldPageW = orientation === 'landscape' ? 297 : 210;
        const oldPageH = orientation === 'landscape' ? 210 : 297;
        const newPageW = next === 'landscape' ? 297 : 210;
        const newPageH = next === 'landscape' ? 210 : 297;
        const wScale = oldPageW / newPageW;
        const hScale = oldPageH / newPageH;
        const scaleBox = (b) => ({
            xPct: b.xPct * wScale,
            yPct: b.yPct * hScale,
            widthPct: b.widthPct * wScale,
            heightPct: b.heightPct * hScale,
        });
        setQrBox((prev) => clampQrBox(scaleBox(prev)));
        setTypeBox((prev) => clampTypeBox(scaleBox(prev)));
        setBgBox((prev) => clampBgBox(scaleBox(prev)));
        // 마진은 % 그대로 두면 물리적으로 달라지므로 동일 mm 보존하게 스케일링.
        setMargins((prev) => clampMargins({
            top: prev.top * hScale,
            bottom: prev.bottom * hScale,
            left: prev.left * wScale,
            right: prev.right * wScale,
        }));
        setOrientation(next);
    };

    return (
        <div>
            {/* 제목 옆에 transient success 메시지(Reset to default / Saved 등)를 인라인 표시.
                별도 alert 배너가 화면을 차지하던 것을 제목 옆으로 이동해 시인성 + 공간 효율 개선. */}
            <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                <h3 className="fs-3 mb-0">{t('license.label.voucherTemplateEditor')}</h3>
                {message && (
                    <span className="text-success small fw-semibold">
                        ✓ {message}
                    </span>
                )}
            </div>

            {(error || upload.error) && (
                <div className="alert alert-danger py-2 mb-2">{error || upload.error}</div>
            )}

            {/* 좌우 2열 레이아웃 — 좁은 화면에서는 자동으로 위아래 stack */}
            <div className="row g-3">
                {/* 왼쪽 컨트롤 패널 — 폭 축소 + 우측 여백 */}
                <div className="col-md-4 col-lg-3 pe-md-3">
                    <div className="d-flex flex-column gap-2 p-3 bg-light rounded">
                        <p className="text-muted small mb-1">
                            {t('license.message.dragQrBoxToPosition')}
                        </p>

                        <div>
                            <label className="form-label small mb-1">
                                {t('license.label.backgroundImage')}
                                <span className="text-muted ms-1">({t('io.label.png')} / {t('io.label.jpeg')}, {t('io.label.maxFileSizeMb')})</span>
                            </label>
                            <input
                                type="file"
                                accept="image/png,image/jpeg"
                                className="form-control form-control-sm"
                                onChange={handleFileChange}
                            />
                        </div>

                        <div>
                            <label className="form-label small mb-1">{t('io.label.orientation')}</label>
                            <select
                                className="form-select form-select-sm"
                                value={orientation}
                                onChange={(e) => handleOrientationChange(e.target.value)}
                            >
                                <option value="portrait">{t('io.label.portrait')}</option>
                                <option value="landscape">{t('io.label.landscape')}</option>
                            </select>
                        </div>

                        <div>
                            <label className="form-label small mb-1">
                                {t('io.label.margins')} <span className="text-muted">(pixel)</span>
                            </label>
                            {/* 라벨 너비를 고정해서 입력 박스 폭이 4개 모두 동일하게 보이도록.
                                Top(3자), Bottom(6자), Left(4자), Right(5자) 등 i18n 길이 차이 흡수.
                                값은 캔버스 px 기준 — 내부 % 상태는 canvasSize 변경 시에도 안정 유지. */}
                            <div className="row g-1">
                                {[
                                    { key: 'top', label: 'Top' },
                                    { key: 'bottom', label: 'Bottom' },
                                    { key: 'left', label: 'Left' },
                                    { key: 'right', label: 'Right' },
                                ].map(({ key, label }) => {
                                    // 축별 캔버스 픽셀 기준 max — 합 제한(45%)과 동일 효과
                                    const maxPx = Math.floor(marginAxis(key) * 0.45);
                                    return (
                                        <div className="col-6" key={key}>
                                            <div className="input-group input-group-sm">
                                                <span
                                                    className="input-group-text"
                                                    style={{ width: '4rem', justifyContent: 'center' }}
                                                >
                                                    {t(label)}
                                                </span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={maxPx || undefined}
                                                    step="1"
                                                    className="form-control"
                                                    value={marginPx(key)}
                                                    onChange={handleMarginChange(key)}
                                                    disabled={!canvasSize.width}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <button
                                className="btn btn-sm btn-outline-primary w-100 mt-2"
                                onClick={handleFit}
                                disabled={!previewUrl || saving}
                                title={t('io.label.fitImageInsideMargins')}
                            >
                                {t('io.label.fit')}
                            </button>
                        </div>

                        <div className="d-flex gap-2 mt-1">
                            <button
                                className="btn btn-sm btn-primary flex-fill"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? t('common.status.saving') : t('common.button.save')}
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary flex-fill"
                                onClick={resetModalCtl.open}
                                disabled={saving}
                            >
                                {t('common.button.resetToDefault')}
                            </button>
                        </div>

                        <div className="small text-muted pt-1 border-top">
                            <strong>QR</strong> x: {qrBox.xPct.toFixed(1)}% / y: {qrBox.yPct.toFixed(1)}% / size: {qrBox.widthPct.toFixed(1)}%
                            <br />
                            <strong>{t('common.field.type')}</strong> x: {typeBox.xPct.toFixed(1)}% / y: {typeBox.yPct.toFixed(1)}% / w: {typeBox.widthPct.toFixed(1)}% / h: {typeBox.heightPct.toFixed(1)}%
                            <br />
                            <strong>{t('common.label.image')}</strong> x: {bgBox.xPct.toFixed(1)}% / y: {bgBox.yPct.toFixed(1)}% / w: {bgBox.widthPct.toFixed(1)}% / h: {bgBox.heightPct.toFixed(1)}%
                        </div>
                    </div>
                </div>

                {/* 오른쪽 캔버스 — A4 비율, 컬럼 폭에 맞춰 최대화. 윈도우가 좁아지면 스크롤 허용.
                    QrPositionOverlay 기본 maxWidth(1024) 보다 크게 잡아 컬럼 폭을 적극 활용. */}
                <div className="col-md-8 col-lg-9">
                    <div style={{ overflowX: 'auto' }}>
                        <QrPositionOverlay
                            qrBox={qrBox}
                            onChange={(next) => setQrBox(next)}
                            maxWidth={orientation === 'landscape' ? 1400 : 900}
                        >
                            <div
                                ref={canvasRef}
                                style={{
                                    width: '100%',
                                    aspectRatio: orientation === 'landscape' ? '297 / 210' : '210 / 297',
                                    backgroundColor: '#f5f5f5',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: '1px solid #ddd',
                                }}
                            >
                                {/* 마진 가이드 — 점선 사각형으로 콘텐츠 영역 시각화. pointer-events:none 으로
                                    Rnd 드래그 방해 금지. zIndex 0 으로 이미지(1)·QR(10) 보다 아래. */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: `${margins.left}%`,
                                        top: `${margins.top}%`,
                                        width: `${Math.max(0, 100 - margins.left - margins.right)}%`,
                                        height: `${Math.max(0, 100 - margins.top - margins.bottom)}%`,
                                        border: '1px dashed rgba(108, 117, 125, 0.6)',
                                        pointerEvents: 'none',
                                        zIndex: 0,
                                        boxSizing: 'border-box',
                                    }}
                                />
                                {previewUrl && canvasSize.width > 0 ? (
                                    // 이미지 Rnd — 비율 유지(lockAspectRatio=imageAspect) 드래그/리사이즈.
                                    // bgBox 의 % 좌표를 캔버스 px 좌표로 환산하여 Rnd 에 전달.
                                    <Rnd
                                        position={{
                                            x: (bgBox.xPct / 100) * canvasSize.width,
                                            y: (bgBox.yPct / 100) * canvasSize.height,
                                        }}
                                        size={{
                                            width: (bgBox.widthPct / 100) * canvasSize.width,
                                            height: (bgBox.heightPct / 100) * canvasSize.height,
                                        }}
                                        bounds="parent"
                                        lockAspectRatio={imageAspect || true}
                                        onDragStop={(_e, d) => {
                                            setBgBox(clampBgBox({
                                                xPct: (d.x / canvasSize.width) * 100,
                                                yPct: (d.y / canvasSize.height) * 100,
                                                widthPct: bgBox.widthPct,
                                                heightPct: bgBox.heightPct,
                                            }));
                                        }}
                                        onResizeStop={(_e, _dir, ref, _delta, pos) => {
                                            setBgBox(clampBgBox({
                                                xPct: (pos.x / canvasSize.width) * 100,
                                                yPct: (pos.y / canvasSize.height) * 100,
                                                widthPct: (ref.offsetWidth / canvasSize.width) * 100,
                                                heightPct: (ref.offsetHeight / canvasSize.height) * 100,
                                            }));
                                        }}
                                        style={{
                                            // QR Rnd 보다 아래로 — z-index 명시
                                            zIndex: 1,
                                            cursor: 'move',
                                        }}
                                    >
                                        <img
                                            src={previewUrl}
                                            alt={t('license.label.voucherTemplatePreview')}
                                            draggable={false}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'fill',
                                                display: 'block',
                                                pointerEvents: 'none',
                                            }}
                                        />
                                    </Rnd>
                                ) : !previewUrl ? (
                                    <div
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#999',
                                        }}
                                    >
                                        {t('license.label.noBackgroundDefault')}
                                    </div>
                                ) : null}
                                {/* 라이선스 type 라벨 박스 — 사용자 드래그/리사이즈로 위치·크기 조절.
                                    인쇄 시 v.type / v.validuntil 로 "Initial Valid Until: YYYY. M. D" 표시.
                                    프리뷰는 1년 후 날짜로 placeholder. SVG textLength 로 박스 폭 fit. */}
                                {canvasSize.width > 0 && (
                                    <Rnd
                                        position={{
                                            x: (typeBox.xPct / 100) * canvasSize.width,
                                            y: (typeBox.yPct / 100) * canvasSize.height,
                                        }}
                                        size={{
                                            width: (typeBox.widthPct / 100) * canvasSize.width,
                                            height: (typeBox.heightPct / 100) * canvasSize.height,
                                        }}
                                        bounds="parent"
                                        onDragStop={(_e, d) => {
                                            setTypeBox(clampTypeBox({
                                                xPct: (d.x / canvasSize.width) * 100,
                                                yPct: (d.y / canvasSize.height) * 100,
                                                widthPct: typeBox.widthPct,
                                                heightPct: typeBox.heightPct,
                                            }));
                                        }}
                                        onResizeStop={(_e, _dir, ref, _delta, pos) => {
                                            setTypeBox(clampTypeBox({
                                                xPct: (pos.x / canvasSize.width) * 100,
                                                yPct: (pos.y / canvasSize.height) * 100,
                                                widthPct: (ref.offsetWidth / canvasSize.width) * 100,
                                                heightPct: (ref.offsetHeight / canvasSize.height) * 100,
                                            }));
                                        }}
                                        style={{
                                            border: '2px dashed #198754',
                                            background: 'rgba(25, 135, 84, 0.1)',
                                            cursor: 'move',
                                            boxSizing: 'border-box',
                                            zIndex: 10,
                                        }}
                                    >
                                        {(() => {
                                            // 프리뷰 SVG. ⚠️ 인쇄본(VoucherPrintArea.renderFitText)과 **동일하지 않다** —
                                            // 인쇄본은 xMidYMin meet / y=VB_H*0.7 / dy=0 으로 baseline 을 보정하고,
                                            // 여기는 xMidYMid meet / y=VB_H/2 / dy=0.35em 이라 수직 위치가 다르다.
                                            // auto-fit 계산(SAFE_CHAR_W/FONT_UNIT/VB_W)도 아래에 재구현돼 있다.
                                            // 통일하려면 renderFitText 를 lib 으로 올려 공유해야 한다(후속).
                                            // IMP-438: 앱 언어 locale 로 렌더(formatLicenseDateOnly SSoT).
                                            const previewDate = formatLicenseDateOnly(Date.now() + 365 * 24 * 60 * 60 * 1000);
                                            // IMP-437 후속: 프리뷰만 영어 하드코딩이던 문제 — 실제 인쇄본
                                            // (VoucherPrintArea.js) 과 동일하게 i18n 키 조합 사용.
                                            // SVG viewBox 는 text.length 기반 auto-fit(preserveAspectRatio)이라
                                            // 번역으로 길이가 달라져도 박스 안에서 축소/확대만 될 뿐 레이아웃이
                                            // 깨지지 않음 — VoucherPrintArea 의 typeBox 텍스트도 동일 패턴으로
                                            // 이미 다국어 렌더링 중.
                                            const text = `${t('license.label.initial')} ${t('license.field.validUntil')}: ${previewDate}`;
                                            const SAFE_CHAR_W = 0.6;
                                            const FONT_UNIT = 10;
                                            const VB_W = text.length * FONT_UNIT * SAFE_CHAR_W;
                                            const VB_H = FONT_UNIT * 1.2;
                                            return (
                                                <svg
                                                    width="100%"
                                                    height="100%"
                                                    viewBox={`0 0 ${VB_W} ${VB_H}`}
                                                    preserveAspectRatio="xMidYMid meet"
                                                >
                                                    <text
                                                        x={VB_W / 2}
                                                        y={VB_H / 2}
                                                        dy="0.35em"
                                                        textAnchor="middle"
                                                        fontSize={FONT_UNIT}
                                                        fontWeight="bold"
                                                        fill="#198754"
                                                    >
                                                        {text}
                                                    </text>
                                                </svg>
                                            );
                                        })()}
                                    </Rnd>
                                )}
                            </div>
                        </QrPositionOverlay>
                    </div>
                </div>
            </div>

            {/* react-bootstrap closeButton 이 프로젝트 CSS 때문에 "×Close" 로 표시되는 이슈 회피 —
                VoucherPrintModal 과 동일하게 수동 btn-close 버튼으로 X 만 렌더. Cancel 버튼은 유지. */}
            <Modal show={resetModalCtl.show} onHide={resetModalCtl.close}>
                <Modal.Header className="d-flex justify-content-between align-items-center">
                    <Modal.Title>{t('common.button.resetToDefault')}</Modal.Title>
                    <button
                        type="button"
                        className="btn-close"
                        aria-label={t('common.button.close')}
                        onClick={resetModalCtl.close}
                    />
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">{t('license.label.resetToDefaultTemplate')}</p>
                    {/* IMP-214 (사용자 보고 2026-05-13): Reset 즉시 BE 에서 기존 설정이 soft-delete 되어
                        저장 행위와 무관하게 데이터가 사라짐. 사용자가 명시적으로 인지하도록 경고 추가. */}
                    <p className="mb-0 text-danger small">
                        {t('license.message.resetVoucherTemplateConfirm')}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-outline-secondary" onClick={resetModalCtl.close}>
                        {t('common.button.cancel')}
                    </button>
                    <button className="btn btn-danger" onClick={resetModalCtl.confirm}>
                        {t('common.button.confirm')}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const mapState = (state, ownProps) => {
    const licenseState = selectLicenseProductState(state, ownProps?.product?.key);
    return {
        user: state.AuthReducer.user,
        voucherTemplate: licenseState.voucherTemplate,
    };
};

export default connect(mapState)(VoucherTemplateEditor);
